import { memo, useEffect, useRef, useState } from "react";
import { useApp } from "../../hooks/AppProvider";

import "../../css/animation/behavior-session.css";
import { AnimatedComponent, ANIMATION_CONTROL } from "../../components/BasicComponents";
import { useNavigate, useParams } from "react-router-dom";

const MemoGauge = memo(({ animationDuration, value, max, play }) => {
    const gaugeRef = useRef(null);
    const keyframes = [
        {
            height: "0%"
        },
        {
            height: (value * 100 / max) + "%"
        }
    ];

    const options = {
        duration: animationDuration,
        fill: "forwards",
        earsing: "linear"
    };

    return (
        <AnimatedComponent
            animatedElementRef={gaugeRef}
            keyframes={keyframes}
            options={options}
            defaultControl={play?ANIMATION_CONTROL.PLAY:ANIMATION_CONTROL.PAUSE}
        >
            <div className="gauge">
                <div ref={gaugeRef} className="gauge-indicator" ></div>
            </div>
        
        </AnimatedComponent>
    );

});

function Gauge({ max, value, totalDuration, label, play }) {
    
    const [currentValue, setCurrentValue] = useState(0.0);

    const animationDuration = (value * totalDuration / max) * 1000;

    useEffect(() => {
        if(!play)
            return;
        
        let curVal = currentValue;
        
        let interval = setInterval(() => {
            setCurrentValue(e => e+1)
        }, (totalDuration / max) * 1000);

        let timeout = setTimeout(() => {
            clearInterval(interval);
            setCurrentValue(value);
        }, animationDuration);
        return () => {clearInterval(interval); clearTimeout(timeout)};
    }, [play]);


    return (
        <div className="gauge-large-container">
            <span className="gauge-label">{label}</span>
            <MemoGauge animationDuration={animationDuration} value={value} max={max} play={play} />
            <span className="gauge-indicator-text">{currentValue} points</span>
        </div>
    );
}

function BehaviorGroupsGauge({ ranks, max, duration, next, terminate }) {

    const navigate = useNavigate();
    const [play, setPlay] = useState(false);
    const [showNext, setShowNext] = useState(false);
    const [showTerminate, setShowTerminate] = useState(false);

    useEffect(() => {
        if(max === 0) {
            console.error("Une erreur s'est produite [100]")
            navigate(next);
        }
    }, [max]);

    useEffect(() => {
        if(play) {
            let timeout = setTimeout(() => {
                if(next !== undefined)
                    setShowNext(true);
                if(terminate !== undefined)
                    setShowTerminate(true);
            }, duration * 1000);

            return () => clearTimeout(timeout);
        }
    }, [play]);

    const onClickPlay = () => {
        setPlay(true);
    };

    const onClickNext = () => {
        navigate(next);
    };

    const onClickTerminate = () => {
        navigate(terminate);
    };

    return (
        <div className="gauges-large-container">
            <div className="gauges-container">
                {ranks.map(rank => (
                    <Gauge value={rank.amount} max={max} totalDuration={duration} label={rank.group_name} key={rank.group_id} play={play} />
                ))}
            </div>
            {!play && (<button onClick={onClickPlay} className="start-button">GO !</button>)}
            {showNext && (<button onClick={onClickNext}>Suivant</button>)}
            {showTerminate && (<button onClick={onClickTerminate}>Terminer</button>)}
        </div>
    )
}

function BehaviorAnimationContainer({ title, duration, max, array, next, terminate }) {

    return (
        <div>
            <header className="header">
                <span className="header-title">{title}</span>
            </header>
            <div className="main-container">
                <BehaviorGroupsGauge ranks={array} duration={duration} max={max} next={next} terminate={terminate} />
            </div>
        </div>
    );
}

function BehaviorSessionAnimation() {
    const app = useApp();

    const [ranks, setRanks] = useState([]);
    const [maximumPoints, setMaximumPoints] = useState(1);

    useEffect(() => {
        app.sendGet("/behavior/session/rank", data => {
            if(data.success === false) {
                alert("Une erreur s'est produite: [0]");
                app.navigate("/app/behavior/points");
                return;
            }

            setRanks(data);
        });

        app.sendGet("/behavior/session/last", data => {
            if(data.success === false) {
                alert("Une erreur s'est produite: [1]");
                app.navigate("/app/behavior/points");
                return;
            }

            setMaximumPoints(data.points)
        });
    }, []);

    return (
        <BehaviorAnimationContainer
            title={"Récapitulatif de la journée"}
            array={ranks}
            duration={10}
            max={maximumPoints}
            next={"/behavior-current-term-animation"}
        />
    );
}

function BehaviorCurrentTermAnimation() {
    const app = useApp();

    const [ranks, setRanks] = useState([]);
    const [maximumPoints, setMaximumPoints] = useState(1);

     useEffect(() => {
        app.sendGet("/behavior/term/current/rank", data => {
            if(data.success === false) {
                console.error("Une erreur s'est produite: [10]");
                app.navigate("/app/behavior/points");
                return;
            }
            setRanks(data);
        });

        app.sendGet("/behavior/term/current/maximum-points", data => {
            if(data.success === false) {
                console.error("Une erreur s'est produite: [11]");
                app.navigate("/app/behavior/points");
                return;
            }

            if(data.maximum <= 0) {
                app.navigate("/app/behavior/points");
                return;
            }

            setMaximumPoints(data.maximum)
        });

    }, []);

     return (
        <BehaviorAnimationContainer
            title={"Récapitulatif de la période en cours"}
            array={ranks}
            duration={10}
            max={maximumPoints}
            terminate={"/app/behavior/points"}
        />
    );
}

function BehaviorTermAnimation() {
    const app = useApp();

    const params = useParams();

    const [termId, setTermId] = useState(params.termId);
    const [term, setTerm] = useState({});
    const [ranks, setRanks] = useState([]);
    const [maximumPoints, setMaximumPoints] = useState(1);

     useEffect(() => {
        app.sendGet("/behavior/term/" + termId, data => {
            if(data.success === false) {
                alert("Une erreur s'est produite: [22]");
                app.navigate("/app/behavior/groups");
                return;
            }
            setTerm(data);
        });

        app.sendPost("/behavior/term/rank", {id: termId}, data => {
            if(data.success === false) {
                alert("Une erreur s'est produite: [20]");
                app.navigate("/app/behavior/groups");
                return;
            }
            setRanks(data);
        });

        app.sendPost("/behavior/term/maximum-points", {id: termId}, data => {
            if(data.success === false) {
                alert("Une erreur s'est produite: [21]");
                app.navigate("/app/behavior/groups");
                return;
            }

            if(data.maximum <= 0) {
                alert("La période est vide");
                app.navigate("/app/behavior/groups");
                return;
            }
            setMaximumPoints(data.maximum)
        });

    }, []);

     return (
        <BehaviorAnimationContainer
            title={"Récapitulatif de la période: " + (term?.name ?? "Période inconnue")}
            array={ranks}
            duration={10}
            max={maximumPoints}
            next={"../students"}
        />
    );
}

export { BehaviorSessionAnimation, BehaviorCurrentTermAnimation, BehaviorTermAnimation }